import {
  addDays,
  differenceInCalendarDays,
  format,
  formatDistanceToNowStrict,
  isSameDay,
  isToday,
  isTomorrow,
  isYesterday,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import { formatLocalDate, parseLocalDate } from '@app/shared';

export { formatLocalDate, parseLocalDate };

export const todayIso = () => formatLocalDate(new Date());

export function weekStart(date: Date, weekStartsOn: 0 | 1 | 6): Date {
  return startOfWeek(date, { weekStartsOn: weekStartsOn as 0 | 1 | 6 });
}

/** "Today", "Tomorrow", "Yesterday", "Mon, Mar 2". */
export function friendlyDay(date: Date): string {
  if (isToday(date)) return 'Today';
  if (isTomorrow(date)) return 'Tomorrow';
  if (isYesterday(date)) return 'Yesterday';
  const days = differenceInCalendarDays(date, new Date());
  if (days > 1 && days < 7) return format(date, 'EEEE');
  return format(date, date.getFullYear() === new Date().getFullYear() ? 'EEE, MMM d' : 'EEE, MMM d, yyyy');
}

export function friendlyDate(iso: string): string {
  return friendlyDay(parseLocalDate(iso));
}

export function timeLabel(date: Date): string {
  return format(date, date.getMinutes() === 0 ? 'h a' : 'h:mm a');
}

/** "18:00" → "6 PM" in the viewer's locale style. */
export function timeOfDayLabel(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return timeLabel(new Date(2000, 0, 1, h, m));
}

export function timeRange(start: Date, end: Date): string {
  if (isSameDay(start, end)) return `${timeLabel(start)} – ${timeLabel(end)}`;
  return `${format(start, 'MMM d')}, ${timeLabel(start)} – ${format(end, 'MMM d')}, ${timeLabel(end)}`;
}

export function relativeTime(iso: string): string {
  const d = new Date(iso);
  if (Date.now() - d.getTime() < 60_000) return 'just now';
  return `${formatDistanceToNowStrict(d)} ago`;
}

export function daysUntil(iso: string): number {
  return differenceInCalendarDays(parseLocalDate(iso), startOfDay(new Date()));
}

export function dueLabel(iso: string): string {
  const n = daysUntil(iso);
  if (n < -1) return `${-n} days overdue`;
  if (n === -1) return 'Yesterday';
  if (n === 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  if (n < 7) return `In ${n} days`;
  return format(parseLocalDate(iso), 'MMM d');
}

/** dueLabel for use mid-sentence: "due tomorrow", "due in 3 days", but "due Oct 13". */
export function dueLabelInline(iso: string): string {
  const label = dueLabel(iso);
  return /^[A-Z][a-z]{2} \d/.test(label) ? label : label.toLowerCase();
}

export function age(birth: string, on = new Date()): number {
  const b = parseLocalDate(birth);
  let years = on.getFullYear() - b.getFullYear();
  if (on.getMonth() < b.getMonth() || (on.getMonth() === b.getMonth() && on.getDate() < b.getDate())) years--;
  return years;
}

/** Next occurrence (today or later) of an annual date like a birthday. */
export function nextAnniversary(iso: string, from = new Date()): Date {
  const b = parseLocalDate(iso);
  const today = startOfDay(from);
  const lastDay = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
  const make = (y: number) => new Date(y, b.getMonth(), Math.min(b.getDate(), lastDay(y, b.getMonth())));
  const thisYear = make(today.getFullYear());
  return thisYear >= today ? thisYear : make(today.getFullYear() + 1);
}

export function formatMoney(cents: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: cents % 100 === 0 ? 0 : 2 }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

export function parseMoneyToCents(input: string): number | null {
  const cleaned = input.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

export { addDays, format, isSameDay, startOfDay };
